import { Divider } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { DatePicker } from '../DatePicker/DatePicker';
import { Chip } from '../../chip/Chip';
import { DateRangePickerProps } from './interface';

export function DateRangePicker({
  className = '',
  startDateProps,
  endDateProps,
  onChange,
  value,
  filterText = '',
  showPredefinedDateChips = false
}: DateRangePickerProps) {
  const [startDate, setStartDate] = useState<number | null>(null);
  const [endDate, setEndDate] = useState<number | null>(null);
  const [selectedChip, setSelectedChip] = useState<string | null>(null);
  useEffect(() => {
    if (value?.value?.length) {
      setStartDate(value.value[0]);
      setEndDate(value.value[1]);
    } else {
      setStartDate(null);
      setEndDate(null);
      setSelectedChip(null);
    }
  }, [value]);

  const startDateChange = (date: dayjs.Dayjs | null) => {
    if (dayjs(date).isValid()) {
      const _startDate = dayjs(date).startOf('day').valueOf();

      setStartDate(_startDate);
      if (dayjs(endDate).isAfter(date) || dayjs(endDate).isSame(date) || !endDate) {
        onChange({ operator: 'RANGE', value: [_startDate, endDate || 0] });
      }
    }
  };

  const endDateChange = (date: dayjs.Dayjs | null) => {
    if (dayjs(date).isValid() && date) {
      const _endDate = dayjs(date).endOf('day').valueOf();
      setEndDate(_endDate);

      if (dayjs(startDate).isBefore(date) || dayjs(startDate).isSame(date) || !startDate) {
        onChange({ operator: 'RANGE', value: [startDate || 0, _endDate] });
      }
    }
  };

  const onChipClickHandler = (chipType: string) => {
    setSelectedChip(chipType);
    switch (chipType) {
      case '7d': {
        const _startDate = dayjs().subtract(6, 'day').startOf('day').valueOf();
        const _endDate = dayjs().endOf('day').valueOf();
        setEndDate(_endDate);
        setStartDate(_startDate);
        onChange({ operator: 'RANGE', value: [_startDate, _endDate] });
        break;
      }
      case '15d': {
        const _startDate = dayjs().subtract(14, 'day').startOf('day').valueOf();
        const _endDate = dayjs().endOf('day').valueOf();
        setEndDate(_endDate);
        setStartDate(_startDate);
        onChange({ operator: 'RANGE', value: [_startDate, _endDate] });
        break;
      }
      case 'today': {
        const _startDate = dayjs().startOf('day').valueOf();
        const _endDate = dayjs().endOf('day').valueOf();
        setEndDate(_endDate);
        setStartDate(_startDate);
        onChange({ operator: 'RANGE', value: [_startDate, _endDate] });
        break;
      }

      case 'yesterday': {
        const _startDate = dayjs().subtract(1, 'day').startOf('day').valueOf();
        const _endDate = dayjs().subtract(1, 'day').endOf('day').valueOf();
        setEndDate(_endDate);
        setStartDate(_startDate);
        onChange({ operator: 'RANGE', value: [_startDate, _endDate] });
        break;
      }
      default:
    }
  };

  return (
    <>
      <div className={clsx('center-flex-row px-4 mt-3', className)}>
        {filterText.length ? <p className='text-nowrap text-body-1 mr-5'>{filterText}</p> : null}
        <DatePicker
          dataId='start-date'
          format='DD-MM-YYYY'
          disableHighlightToday
          value={startDate ? dayjs(startDate) : null}
          onChange={startDateChange}
          {...startDateProps}
        />
        <Divider
          orientation='horizontal'
          variant='middle'
          sx={{ borderColor: 'black', width: '6px', margin: '8px' }}
        />
        <DatePicker
          dataId='end-date'
          disableHighlightToday
          format='DD-MM-YYYY'
          value={endDate ? dayjs(endDate) : null}
          onChange={endDateChange}
          {...endDateProps}
        />
      </div>
      {showPredefinedDateChips ? (
        <div data-id='predefined-chips' className='mt-5 mb-1 center-flex-row'>
          <Chip
            dataId='today'
            label='Today'
            onClick={() => {
              onChipClickHandler('today');
            }}
            selected={selectedChip === 'today'}
            className='ml-4'
          />
          <Chip
            dataId='yesterday'
            label='Yesterday'
            onClick={() => {
              onChipClickHandler('yesterday');
            }}
            selected={selectedChip === 'yesterday'}
            className='ml-4'
          />
          <Chip
            dataId='7d'
            label='Past 7 days'
            onClick={() => {
              onChipClickHandler('7d');
            }}
            selected={selectedChip === '7d'}
            className='ml-4'
          />
          <Chip
            dataId='15d'
            label='Past 15 days'
            onClick={() => {
              onChipClickHandler('15d');
            }}
            selected={selectedChip === '15d'}
            className='mx-4'
          />
        </div>
      ) : null}
    </>
  );
}
